import get from 'lodash/get'

import { userIsAuthorised } from '../../van-shared/utils/auth'
import { hasFeature } from '../../van-shared/utils/features'
import { hasUserContract } from '../../subapps/settings/common/utils'
import capitalize from '../../common/utils/capitalize'
import { isLastMileDeliveryUser } from '../../subapps/shipments/common/utils'

import {
  isPsmCarrierUser,
  isPsmResellerNationalUser,
  isPsmResellerWarehouseUser,
  isShelflifeBasedApp
} from '../../van-shared/utils/utils'

/**
 * FIXME: This is an interim hack around the shortcomings of the current
 * authentication framework.
 *
 * See: https://github.com/fielded/docs/pull/63
 */
const userHasSubRole = (user, role) => user.roles.find(r => r.startsWith(role))

export const getAdministrationSubitems = (config, user) => {
  const isShelflifeBased = isShelflifeBasedApp(config)
  const features = get(config, 'features')
  const administrationItems = []

  const isRetailer = userIsAuthorised(user, 'feature:userRole:pharmacyUser')
  const isPsmCarrier = isPsmCarrierUser(user)
  const hasUserAdmin = hasFeature(features, 'settings:users') &&
    userIsAuthorised(user, 'feature:settings:users')
  const hasRouteAdminSL = isShelflifeBased && hasFeature(features, 'settings:routes:edit') &&
    userIsAuthorised(user, 'feature:settings:funders')
  const hasFunderAdminPSM = hasFeature(features, 'settings:funders:edit') &&
    userIsAuthorised(user, 'feature:funders')

  // Shelf Life pharmacy users have access to some parts of location admin
  // but not to others:
  // - they can see contracts and subscriptions but not location details
  // - they should see a different item in the main menu
  // (instead of Administration > Locations)
  const hasLocationAdmin = hasFeature(features, 'settings:facilities') &&
    (userHasSubRole(user, 'feature:settings:facilities') || userIsAuthorised(user, 'feature:settings:facilities')) && !isRetailer

  const hasProductsAdmin = hasFeature(features, 'settings:products') &&
    (userHasSubRole(user, 'feature:settings:products') || userIsAuthorised(user, 'feature:settings:products'))
  const hasPlanningCalculator = hasFeature(features, 'settings:planningCalculator') &&
    (userHasSubRole(user, 'feature:settings:planning-calculator') || userIsAuthorised(user, 'feature:settings:planning-calculator'))
  const hasPrograms = hasFeature(features, 'settings:programs') && userIsAuthorised(user, 'feature:create-programs') && userIsAuthorised(user, 'feature:userRole:psm-national')
  const hasRoutePlanning = (
    hasFeature(features, 'routePlanning') &&
    (userIsAuthorised(user, 'feature:routePlanning:edit') || userIsAuthorised(user, 'feature:routePlanning:view') || isPsmCarrier)
  )
  const hasStockUpdate = (hasFeature(features, 'settings:stockUpdate.enabled') || hasFeature(config, 'features.stockUpdate')) &&
    (userIsAuthorised(user, 'feature:orders:warehouse') || (userIsAuthorised(user, 'feature:userRole:psm-facility')))

  const hasCarrierManagement = hasFeature(features, 'routePlanning') && userIsAuthorised(user, 'feature:carrierManagement')

  // Only tenants that add 'order payers' should see this item
  const hasOrganisationManagement = hasFeature(features, 'orders.payerOrgSelection') &&
    userIsAuthorised(user, 'feature:organisation-management')

  if (hasRoutePlanning) {
    administrationItems.push({ path: '/settings/routePlanning', label: 'Routes' })
  }

  if (hasLocationAdmin) {
    administrationItems.push({ path: '/settings/facilities', label: 'Locations' })
  }

  if (hasCarrierManagement) {
    administrationItems.push({ path: '/settings/carriers', label: 'Carriers' })
  }

  if (hasOrganisationManagement) {
    administrationItems.push({ path: '/settings/organisations', label: 'Organisations' })
  }

  if (hasUserAdmin) {
    administrationItems.push({ path: '/settings/users', label: isPsmCarrier ? 'Drivers' : 'Users' })
  }

  if (hasProductsAdmin) {
    administrationItems.push({ path: '/settings/products', label: `${isShelflifeBased ? 'Product price lists' : 'Products'}` })
  }

  if (hasStockUpdate) {
    administrationItems.push({ path: '/settings/stock-update', label: 'Stock update' })
  }

  if (hasPlanningCalculator) {
    administrationItems.push({ path: '/settings/planning-calculator', label: 'Planning calculator' })
  }

  if (hasPrograms) {
    administrationItems.push({ path: '/settings/programs', label: 'Programs' })
  }

  if (hasRouteAdminSL) {
    const routeName = get(features, 'settings.routes.noun')
    administrationItems.push({ path: `/settings/${routeName}s`, label: `${capitalize(routeName)}s` })
  }

  if (hasFunderAdminPSM) {
    const funderName = get(features, 'settings.funders.noun')
    administrationItems.push({ path: `/settings/${funderName}s`, label: `${capitalize(funderName)}s` })
  }

  return administrationItems
}

export const getMyAccountSubitems = (user, isBasicUser, isLeadUser, isShelflifeReseller, pendingContracts, outdatedContracts) => {
  const myAccountItems = []

  let contractsToSignAmount = pendingContracts
  if (outdatedContracts) {
    contractsToSignAmount = pendingContracts + outdatedContracts.length
  }

  const pendingContractsBadge = contractsToSignAmount > 0 ? contractsToSignAmount.toString() : undefined

  const isRetailer = userIsAuthorised(user, 'feature:userRole:pharmacyUser')
  const driverWithContracts = hasUserContract(user)
  const canViewSourceLedger = userIsAuthorised(user, 'feature:can-view-source-ledger')
  const isPsmCarrier = isPsmCarrierUser(user)

  myAccountItems.push(
    { path: '/profile', label: 'My profile' }
  )

  if (isRetailer && !canViewSourceLedger) {
    myAccountItems.push(
      { path: '/retailer/profile/plan', label: 'My plan' }
    )
    if (!isBasicUser && !isLeadUser && !isShelflifeReseller) {
      myAccountItems.push(
        { path: '/retailer/profile/contracts', label: 'Contracts', badgeLabel: pendingContractsBadge }
      )
    }
  }

  if (driverWithContracts) {
    myAccountItems.push(
      { path: '/profile/fp-agreement', label: 'Contracts', badgeLabel: pendingContractsBadge }
    )
  }

  if (isPsmCarrier && user.organisationId) {
    const organisationId = user.organisationId

    myAccountItems.push(
      { path: `/carrier/profile/${organisationId}`, label: 'Carrier profile' }
    )
  }

  return myAccountItems
}

export const getAnalyticsSubitems = (features, user) => {
  let analyticsItems

  const hasForecasting = (
    hasFeature(features, 'forecasting:enable') && userIsAuthorised(user, 'feature:forecasting')
  )

  if (hasForecasting) {
    analyticsItems = [
      { path: '/analytics', label: 'Analytics' },
      { path: '/analytics/forecast', label: 'Forecasting' }
    ]
  }

  return analyticsItems
}

export const getShipmentsSubitems = (config, user) => {
  const features = config.features
  const isPsmResellerWarehouse = isPsmResellerWarehouseUser(config, user)

  const hasShipments = hasFeature(features, 'shipments') && userIsAuthorised(user, 'feature:shipments')
  const hasLastMileDeliveries = hasFeature(features, 'lastMileDeliveries') && isLastMileDeliveryUser(user)
  const hasShipmentUploadRole = userIsAuthorised(user, 'feature:uploadShipments')
  const hasWarehouseShipmentAccess = isPsmResellerWarehouse || hasShipmentUploadRole
  const hasShipmentUploadAccess = hasFeature(features, 'shipments:uploadShipments') && hasWarehouseShipmentAccess

  let shipmentsItems = []

  // Either role opens the Shipments menu. Users who only hold
  // `feature:lastMileDeliveries` (carriers) get the last mile deliveries link
  // and nothing else — the National shipments branch below still asks for both.
  if (hasShipments || hasLastMileDeliveries || hasShipmentUploadAccess || isPsmResellerWarehouse) {
    // Show the shipment list only if the user is NOT a PSM reseller national user.
    // Note: All national users currently have both 'feature:shipments' and 'feature:global-shipments' roles,
    // but PSM reseller national users (e.g., EPN) should be excluded from this view.
    // TODO: Revisit this logic when these roles are no longer mandatory for national users.
    if (!isPsmResellerNationalUser(config, user)) {
      if (hasShipmentUploadAccess) {
        shipmentsItems.push({ path: '/shipments/list', label: 'Inbound shipments' })
      } else if (!hasWarehouseShipmentAccess && hasShipments && hasLastMileDeliveries) {
        // Warehouse-like users on tenants without uploadShipments (e.g. RMS) get
        // no list link — that's the intent of the override.
        shipmentsItems.push({ path: '/shipments/list', label: 'National shipments' })
      }
    }

    if (hasLastMileDeliveries || hasShipmentUploadAccess) {
      shipmentsItems.push({ path: '/shipments/last-mile-deliveries', label: 'Last mile deliveries' })
    }

    if (isPsmResellerWarehouse) {
      shipmentsItems.push({ path: '/shipments/pick-up', label: 'Self-pickup' })
    }

    if (hasShipmentUploadAccess) {
      shipmentsItems.push({ path: '/shipments/export-import/download', label: 'Outbound shipments' })
    }

    if (isPsmResellerWarehouse) {
      shipmentsItems.push({ path: '/shipments/stock-transfers', label: 'Stock Transfers' })
    }
  }

  return shipmentsItems
}

export const getOrdersSubitems = (config, user) => {
  const orderEntityName = get(config, 'features.order.appEntityName', 'Order')
  const isWarehouseUser = userIsAuthorised(user, 'feature:orders:warehouse')

  const ordersItems = [{
    path: '/orders/',
    label: `${orderEntityName} summaries`,
    isActiveSlug: '/program'
  }]

  // TODO: remove top up import code or fix it.
  // Temporarily disable topup import for orders user
  // and instead use create for location in order/create
  const disableOrderBulk = get(config, 'features.order.disableBulk', false)
  if (user.roles.some(role => role.includes('orders:bulk')) && !disableOrderBulk) {
    ordersItems.push({
      path: '/orders/bulk',
      label: 'Export & Import',
      isActiveSlug: '/orders/bulk'
    })

    if (isWarehouseUser) {
      ordersItems.push({
        path: '/orders/edit-allocation',
        label: 'Edit Allocation',
        isActiveSlug: '/orders/edit-allocation'
      })
    }
  }

  return ordersItems
}
